/**
 * Player.ts — The dog walker.
 *
 * Movement model (unchanged from the 2D version): each frame the player computes a
 * *desired* input velocity from WASD / arrow keys. That velocity is NOT applied
 * here — the Leash system combines it with the spring pull and writes the final
 * velocity (see systems/Leash.ts). That's how "the dog's pull alters the player's
 * movement vector": input + spring pull → resulting motion.
 */
import * as THREE from 'three';
import { PLAYER, ANIM } from '../config/constants';
import { Entity } from './Entity';
import { isMoving } from './locomotion';
import { createPlayer, createBag, animateWalk, type CharacterRig } from '../render/models';
import type { Input } from '../core/Input';
import type { Vec2 } from '../utils/vec2';

export class Player extends Entity {
  private rig: CharacterRig;
  private bag?: THREE.Object3D;

  constructor(
    x: number,
    y: number,
    private input: Input,
  ) {
    const rig = createPlayer();
    super(x, y, PLAYER.RADIUS, rig.group);
    this.rig = rig;
  }

  /**
   * Desired input velocity (units/s) for this frame. The Leash system reads
   * this and adds the spring pull on top.
   *
   * Nothing slows the walker down any more: the fox tangle that used to halve
   * their speed is gone, and the dog is now the only thing between them and
   * where they are steering. That's deliberate — a walk you lose because your
   * input stopped working is not the same game as one you lose because the dog
   * out-argued you.
   */
  getDesiredVelocity(out: Vec2): Vec2 {
    this.input.getDirection(out);
    out.x *= PLAYER.SPEED;
    out.y *= PLAYER.SPEED;
    return out;
  }

  /** Show/hide the carried poop bag (auto-acquired after the cleanup animation). */
  setCarryingBag(carrying: boolean): void {
    if (carrying && !this.bag) {
      this.bag = createBag();
      // Hangs off the walker's left arm, so it swings with the walk cycle. The
      // offsets in createBag() are measured from the shoulder pivot. createPlayer()
      // always builds both arms, so there is nothing to fall back to.
      this.rig.arms[0].add(this.bag);
    } else if (!carrying && this.bag) {
      this.bag.removeFromParent();
      this.bag = undefined;
    }
  }

  /** Drive the walk cycle + facing from the velocity the leash produced. */
  updateVisuals(dt: number): void {
    this.advanceAnimation(dt);
    animateWalk(
      this.rig,
      this.walkPhase,
      isMoving(this.vel.x, this.vel.y),
      ANIM.PLAYER_LEG_SWING,
      ANIM.PLAYER_BOB,
    );
    this.syncMesh();
  }
}
